// Role and permission names come from the API as dynamic data. This module holds
// only display-formatting helpers — never a hard-coded list of "the" roles or
// permissions — per the product requirement that roles/permissions are data, not code.

// Known API values that arrive without spaces (e.g. "OrganizationApprover") get a
// friendlier label. Anything not in this map is formatted generically instead.
const KNOWN_LABELS = {
  OrganizationApprover: "Organization Approver",
  OrganizationOwner: "Organization Owner",
};

// Inserts spaces before capital letters that follow a lowercase letter, e.g.
// "OrganizationApprover" -> "Organization Approver", so unmapped PascalCase
// values returned by the API still read naturally.
const splitPascalCase = (value) => value.replace(/([a-z])([A-Z])/g, "$1 $2");

export const formatRoleLabel = (rawRoleName) => {
  if (!rawRoleName) return "Unknown role";
  return KNOWN_LABELS[rawRoleName] || splitPascalCase(rawRoleName);
};

// Permission strings use a colon-delimited convention, e.g. "user:copilot:usage:read".
// The first segment is the resource namespace used to group permissions in the UI.
const KNOWN_NAMESPACES = new Set(["platform", "organization", "user"]);

export const getPermissionNamespace = (permission) => {
  if (!permission || typeof permission !== "string") return "other";
  const [first] = permission.split(":");
  return KNOWN_NAMESPACES.has(first) ? first : "other";
};

const NAMESPACE_LABELS = {
  platform: "Platform",
  organization: "Organization",
  user: "User",
  other: "Other",
};

export const formatNamespaceLabel = (namespace) => NAMESPACE_LABELS[namespace] || namespace;

// Groups a flat permission list by resource namespace, preserving input order
// within each group. Returns an array (not a map) so groups render in a stable order.
export const groupPermissionsByNamespace = (permissions) => {
  const groups = new Map();
  (permissions || []).forEach((permission) => {
    const namespace = getPermissionNamespace(permission);
    if (!groups.has(namespace)) groups.set(namespace, []);
    groups.get(namespace).push(permission);
  });

  const order = ["platform", "organization", "user", "other"];
  return order
    .filter((namespace) => groups.has(namespace))
    .map((namespace) => ({
      namespace,
      label: formatNamespaceLabel(namespace),
      permissions: groups.get(namespace),
    }));
};

// A permission ending in ":*" grants everything under that prefix, e.g.
// "platform:*" implies "platform:read", "platform:announcement:read", etc.
// Used to auto-select (and lock) the permissions a wildcard already covers.
export const isWildcardPermission = (permission) => permission?.endsWith(":*") ?? false;

export const getWildcardPrefix = (wildcardPermission) => wildcardPermission.slice(0, -1); // "platform:*" -> "platform:"

export const isImpliedByWildcard = (permission, wildcardPermission) => {
  if (permission === wildcardPermission) return false;
  return permission.startsWith(getWildcardPrefix(wildcardPermission));
};

// True if `grantedPermissions` (a role's or user's effective permission set)
// grants `requiredPermission`, either directly or via a covering wildcard
// (e.g. granted "auth:*" satisfies required "auth:role-assignment:read").
// Unlike isImpliedByWildcard, an exact match here always counts — this
// answers "is X granted", not "is X redundant given some other checked box".
//
// `requiredPermission` may ALSO be a wildcard (e.g. "organization:id:*"),
// meaning "any permission under this prefix satisfies the check" — used
// where a control only cares that SOME organization:id:* permission is held
// (today: read or update), not a specific one. This is a required-side
// wildcard, the mirror case of the granted-side one above: a granted
// "organization:id:*" trivially satisfies it (falls out of the prefix check
// below), but so does holding just "organization:id:read" alone, which the
// granted-side-only check above cannot express.
export const permissionSetGrants = (grantedPermissions, requiredPermission) => {
  if (!grantedPermissions) return false;
  const granted = grantedPermissions instanceof Set ? grantedPermissions : new Set(grantedPermissions);
  if (granted.has(requiredPermission)) return true;

  if (isWildcardPermission(requiredPermission)) {
    const requiredPrefix = getWildcardPrefix(requiredPermission);
    for (const permission of granted) {
      // A non-wildcard permission (or an equally/more-specific wildcard)
      // under the required prefix satisfies it directly (e.g. granted
      // "organization:id:read" or "organization:id:*" satisfies required
      // "organization:id:*"). A BROADER granted wildcard also satisfies it
      // even though its string doesn't start with the required prefix (e.g.
      // granted "organization:*" satisfies required "organization:id:*") —
      // checked via the reverse startsWith.
      if (permission.startsWith(requiredPrefix)) return true;
      if (isWildcardPermission(permission) && requiredPrefix.startsWith(getWildcardPrefix(permission))) return true;
    }
    return false;
  }

  for (const permission of granted) {
    if (isWildcardPermission(permission) && requiredPermission.startsWith(getWildcardPrefix(permission))) {
      return true;
    }
  }
  return false;
};

// Looks up `roleName` in a GET /roles list and checks whether THAT role's own
// permissions[] grant `permissionKey` — the same "check the specific role's
// own permissions, not a global union" principle as useEffectiveOrgPermission,
// but for callers (e.g. a page listing many orgs at once) that already know
// the role name for each row and can't call a single-orgId hook per row.
export const roleGrantsPermission = (rolesList, roleName, permissionKey) => {
  const role = rolesList?.find((r) => r.role_name === roleName);
  return permissionSetGrants(role?.permissions, permissionKey);
};

// Given the set of currently-checked permissions, returns the subset of
// `candidates` that are implied (and therefore should be force-checked and
// disabled) by any checked wildcard permission among `candidates` itself.
export const getWildcardImpliedPermissions = (checkedPermissions, candidates) => {
  const activeWildcards = candidates.filter(
    (p) => isWildcardPermission(p) && checkedPermissions.has(p)
  );
  if (activeWildcards.length === 0) return new Set();

  const implied = new Set();
  candidates.forEach((permission) => {
    if (activeWildcards.some((wildcard) => isImpliedByWildcard(permission, wildcard))) {
      implied.add(permission);
    }
  });
  return implied;
};

// Builds a GitHub-scopes-style tree from a flat permission list: each
// wildcard (e.g. "auth:*") becomes a parent node, nesting any more specific
// wildcard that falls under its prefix (e.g. "auth:role-assignment:*") as a
// child node — recursively — with non-wildcard permissions as leaves. Returns
// { parents: TreeNode[], leaves: string[] } where TreeNode is
// { permission, children: TreeNode[], leaves: string[] }, and a permission
// never appears under more than one parent (it nests under the most specific
// wildcard whose prefix matches it).
export const buildPermissionTree = (permissions) => {
  const wildcards = permissions.filter(isWildcardPermission);
  const nonWildcards = permissions.filter((p) => !isWildcardPermission(p));

  // Most specific (longest prefix) first, so a permission claims the tightest match.
  const sortedWildcards = [...wildcards].sort((a, b) => b.length - a.length);

  const buildNode = (wildcard, remainingWildcards) => {
    const prefix = getWildcardPrefix(wildcard);
    const childWildcards = remainingWildcards.filter(
      (w) => w !== wildcard && w.startsWith(prefix)
    );
    const otherWildcards = remainingWildcards.filter((w) => !childWildcards.includes(w));

    // Only the most specific of the child wildcards become direct children;
    // deeper ones nest recursively inside those via the same rule.
    const directChildWildcards = childWildcards.filter(
      (w) => !childWildcards.some((other) => other !== w && w.startsWith(getWildcardPrefix(other)))
    );

    const leaves = nonWildcards.filter((p) => {
      if (!p.startsWith(prefix)) return false;
      const claimedByChild = directChildWildcards.some((w) => p.startsWith(getWildcardPrefix(w)));
      return !claimedByChild;
    });

    return {
      permission: wildcard,
      children: directChildWildcards.map((w) => buildNode(w, otherWildcards)),
      leaves,
    };
  };

  const topLevelWildcards = sortedWildcards.filter(
    (w) => !sortedWildcards.some((other) => other !== w && w.startsWith(getWildcardPrefix(other)))
  );

  const parents = topLevelWildcards.map((w) => buildNode(w, sortedWildcards));

  const claimedByAnyWildcard = new Set();
  const collectClaimed = (node) => {
    node.leaves.forEach((l) => claimedByAnyWildcard.add(l));
    node.children.forEach(collectClaimed);
  };
  parents.forEach(collectClaimed);

  const leaves = nonWildcards.filter((p) => !claimedByAnyWildcard.has(p));

  return { parents, leaves };
};
